/**
 * 템플릿 미리보기.
 *
 * **iframe 안에서 렌더한다.** 템플릿 CSS 는 1920×1080 캔버스를 전제로 한 전역
 * 스타일이라, 콘솔 문서에 그대로 주입하면 콘솔 자체의 스타일과 부딪힌다.
 * iframe 은 문서를 완전히 격리하므로 그 충돌이 원천적으로 없다.
 *
 * 렌더 함수는 플레이어와 **같은 것**(`@signageone/core`)을 쓴다 — 미리보기에서 본
 * 그림과 화면에 나가는 그림이 다르면 미리보기가 있으나 마나다.
 *
 * 반반 화면의 HTML 칸(2026-11-03)은 이 문서 안에 띄우지 않는다 — 이 iframe 은 스크립트를 막아 두었고, 그 안에 띄운 칸은
 * 그 막힘을 물려받는다. 그래서 렌더러가 남긴 자리(`data-so-html`)를 재서 **그 위에** 올린 HTML 미리보기(`HtmlFrame`)를
 * 겹친다 — 칸 크기(캔버스 좌표)를 화면 삼아 그리므로 재생 화면과 같은 계산이다.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  brandFontFaceCss,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  headerPlace,
  itemHtmlFit,
  renderTemplateStage,
  HTML_SLOT_AREA_ATTR,
  HTML_SLOT_FIT_ATTR,
  parseHtmlSlotArea,
  SPLIT_HTML_ATTR,
  TEMPLATE_CSS,
  TEMPLATE_ENTER_CLASS,
  TEMPLATE_LIVE_CLASS,
  templateBrandCss,
  templateLiveKey,
  templateMediaIds,
  templatePayloadSchema,
  type HtmlArea,
  type TemplatePayload,
} from '@signageone/core';
import { TEMPLATE_FONT_FACES } from './templateFonts.js';
import { useBrand, type BrandLook } from './brand.js';
import { useMediaInfos } from './mediaSource.js';
import { HtmlFrame } from './HtmlContent.js';

/**
 * 미리보기 문서 안 HTML 칸의 자리 — 미리보기 상자 기준(배율이 걸린 뒤)과 칸의 캔버스 크기, 둥근 모서리, 렌더러가 칸에 적은
 * 맞춤·보일 영역.
 */
export interface HtmlSlotBox {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
  pane: { width: number; height: number };
  radius: number;
  fit: 'cover' | 'contain';
  area: HtmlArea | null;
}

/**
 * 미리보기 문서의 HTML 칸을 잰다. 칸의 크기는 배율이 걸리기 전 것(`offsetWidth`)이 캔버스 좌표다. 구역 나누기에서 지금 보이지
 * 않는 장(2026-11-03, 감춘 장도 자리는 지킨다)의 칸은 겹치지 않는다.
 */
export function measureHtmlSlots(doc: Document): HtmlSlotBox[] {
  return Array.from(doc.querySelectorAll<HTMLElement>(`[${SPLIT_HTML_ATTR}]`)).flatMap((slot) => {
    const rect = slot.getBoundingClientRect();
    const pane = { width: slot.offsetWidth, height: slot.offsetHeight };
    if (pane.width <= 0 || pane.height <= 0 || rect.width <= 0) return [];
    if (slot.closest('.so-zone__slide:not(.so-zone__slide--on)')) return [];
    // 둥근 모서리는 칸을 담은 상자의 것 — 구역 나누기는 장(`so-zone__slide`)이 아니라 구역이 둥글다.
    const frame = slot.closest<HTMLElement>('.so-zone') ?? slot.parentElement;
    const corner = frame ? parseFloat(getComputedStyle(frame).borderTopLeftRadius) || 0 : 0;
    return [
      {
        id: slot.getAttribute(SPLIT_HTML_ATTR) ?? '',
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        pane,
        radius: corner * (rect.width / pane.width),
        fit: slot.getAttribute(HTML_SLOT_FIT_ATTR) === 'contain' ? 'contain' : 'cover',
        area: parseHtmlSlotArea(slot.getAttribute(HTML_SLOT_AREA_ATTR)),
      },
    ];
  });
}

const sameSlots = (a: readonly HtmlSlotBox[], b: readonly HtmlSlotBox[]): boolean =>
  a.length === b.length && a.every((slot, index) => JSON.stringify(slot) === JSON.stringify(b[index]));

interface Props {
  template: TemplatePayload;
  /**
   * 헤더에 표시할 장소. 실제 화면이면 그 화면의 머리글 글(`headerPlace` 로 정한 것)을 넣으면 배포 후 모습과 같아진다. 화면이
   * 아닌 미리보기("미리보기")는 환경 설정의 방식을 따른다 — 직접 입력이면 그 글, 표시 안 함이면 빈 글(2026-11-03).
   */
  location?: string;
  timezone?: string;
  /** 미리보기 시각. 지정하면 그 시각의 화면을 본다("내일 아침 9시엔?"). */
  at?: Date;
  /** 초 단위로 시계·카운트다운을 갱신할지. 목록의 작은 썸네일에서는 끈다. */
  live?: boolean;
  /**
   * 16:9 를 고집하지 않고 **담긴 상자를 채운다.**
   *
   * 모니터링 벽이 카드를 화면의 실제 비로 세우기 시작하면서 필요해졌다. 세로 상자에
   * 16:9 를 밀어 넣으면 상자 밖으로 넘치거나 상자가 늘어난다. 여기서 비를 놓아 주면
   * 안쪽 렌더러가 남는 공간을 letterbox 로 채우는데, 그것이 **화면에서 일어나는 일과
   * 같다** — 플레이어도 회전을 안 걸면 같은 자리에 여백을 남긴다.
   */
  fill?: boolean;
  /**
   * 브랜드(머리글 이름·로고·주 색·사내 글꼴). 주지 않으면 환경 설정의 것을 쓴다 — 환경 설정 화면이
   * 저장 전의 고친 값을 미리 보여 줄 때만 준다.
   */
  brand?: BrandLook;
  /**
   * 바뀔 때마다 나타날 때의 움직임을 다시 보인다(2026-11-03) — 편집기의 [움직임 다시 보기]. 살아 있는 미리보기(`live`)는 열 때
   * 한 번 저절로 보인다.
   */
  replay?: number;
}

/** 나타날 때의 움직임을 보이는 동안(ms) — 가장 늦은 진행 막대가 1.4초에 멈춘다(`TEMPLATE_ENTER_CLASS`). */
const ENTER_MS = 1800;

export function TemplatePreview({
  template,
  location,
  timezone,
  at,
  live = false,
  fill = false,
  brand: brandOverride,
  replay = 0,
}: Props): ReactNode {
  const { brand: saved } = useBrand();
  const brand = brandOverride ?? saved;
  const hostRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [tick, setTick] = useState(0);
  /*
   * 나타날 때의 움직임(2026-11-03) — 살아 있는 미리보기만 열 때 한 번(목록 썸네일이 한꺼번에 움직이면 어지럽다). 그동안은 초
   * 갱신을 멈춘다 — 문서를 다시 쓰면 움직임이 처음부터 다시 돈다.
   */
  const [entering, setEntering] = useState(live);
  useEffect(() => {
    if (replay > 0) setEntering(true);
  }, [replay]);
  useEffect(() => {
    if (!entering) return;
    const timer = setTimeout(() => setEntering(false), ENTER_MS);
    return () => clearTimeout(timer);
  }, [entering]);
  const motion = entering ? TEMPLATE_ENTER_CLASS : live ? TEMPLATE_LIVE_CLASS : '';
  // 반반 화면·구역 나누기의 그림·영상·HTML — 콘솔의 미리보기 주소로(받기 전에는 장식 무늬). HTML 은 기준 크기도 받는다.
  const infos = useMediaInfos(useMemo(() => templateMediaIds(template), [template]));
  const media = useMemo(
    () => Object.fromEntries(Object.entries(infos).map(([id, info]) => [id, info.preview_url as string])),
    [infos],
  );
  const [htmlSlots, setHtmlSlots] = useState<HtmlSlotBox[]>([]);

  // 컨테이너 크기를 관찰해 캔버스 배율을 정한다.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  // 살아 있는 미리보기는 초마다 다시 그려 본다 — 시계·카운트다운이 멈춰 있으면
  // 화면이 죽은 것처럼 보인다. 문서는 시각 부분만 고친다(`patchLiveParts`).
  useEffect(() => {
    if (!live || at || entering) return;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [live, at, entering]);

  /*
   * 오류를 state 가 아니라 **memo 의 결과로** 낸다. 렌더 중 setState 는 같은
   * 컴포넌트라도 다시 그리기를 한 번 더 부르고, 조건이 어긋나면 반복된다.
   * 계산 결과에 담으면 그런 여지가 없다.
   */
  const { html: document_, error: renderError } = useMemo<{ html: string; error: string | null }>(() => {
    if (size.width === 0) return { html: '', error: null };
    const now = at ?? new Date();
    void tick; // 초 갱신 트리거

    /*
     * **스키마를 통과시킨 뒤에 그린다.**
     *
     * API 가 준 JSON 에서 날짜는 문자열이다. 렌더러는 `Date` 를 기대하므로 그대로
     * 넘기면 `deadline.getTime is not a function` 으로 터지고, 렌더 중 예외라
     * React 가 트리를 통째로 버려 **콘솔 전체가 빈 화면**이 된다. 실제로 그렇게
     * 터졌다. 스키마가 계약이고, 계약을 지나야 렌더러에 닿는다.
     */
    const parsed = templatePayloadSchema.safeParse(template);
    if (!parsed.success) {
      return { html: '', error: '이 템플릿의 내용이 형식과 맞지 않습니다' };
    }

    let stage: string;
    try {
      stage = renderTemplateStage(
        parsed.data,
        {
          // 이름·위치 방식이면 넘겨받은 글(화면이 아니면 "미리보기"), 직접 입력·표시 안 함이면 그 설정대로.
          location: headerPlace(brand.place, { name: location ?? '미리보기', location: location ?? '미리보기' }),
          now,
          timezone: timezone ?? 'Asia/Seoul',
          brand: brand.name,
          logoUrl: brand.logoUrl,
          media,
        },
        size,
      );
    } catch (error) {
      // 렌더러가 터져도 미리보기 한 칸만 비운다. 화면 전체를 잃지 않는다.
      return { html: '', error: error instanceof Error ? error.message : '미리보기를 만들 수 없습니다' };
    }

    return {
      html:
        `<!doctype html><html lang="ko"><head><meta charset="utf-8">` +
        // 폰트는 콘솔과 같은 것을 쓰되, 없으면 시스템 스택으로 떨어진다.
        `<style>html,body{margin:0;padding:0;overflow:hidden;background:#0f172a}${TEMPLATE_FONT_FACES}\n${TEMPLATE_CSS}</style>` +
        // 브랜드 — 화면(플레이어)도 기본 시트 뒤에 같은 두 장을 건다.
        `<style>${brand.font ? brandFontFaceCss(brand.font.url, brand.font.mime) : ''}\n${templateBrandCss(brand.accent)}</style>` +
        // 나타날 때의 움직임 동안은 so-enter, 그 뒤 살아 있는 미리보기는 떠 있는 동안의 움직임(so-live) — 화면과 같다.
        `</head><body${motion ? ` class="${motion}"` : ''}>${stage}</body></html>`,
      error: null,
    };
  }, [template, location, timezone, at, size, tick, brand, media, motion]);

  // srcdoc 을 직접 쓰지 않고 문서에 써 넣는다 — srcdoc 은 갱신마다 iframe 을
  // 다시 만들어 깜빡인다.
  const [writeError, setWriteError] = useState<string | null>(null);
  /** 마지막으로 써 넣은 문서에서 시각 따라 바뀌는 부분을 비운 글(`templateLiveKey`) — 같으면 다시 쓰지 않고 고친다. */
  const written = useRef<{ doc: Document; key: string } | null>(null);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !document_) return;

    // 접근이 막히면 미리보기가 조용히 비어 버린다 — 원인을 드러낸다.
    let doc: Document | null = null;
    try {
      doc = frame.contentDocument;
    } catch {
      doc = null;
    }
    if (!doc) {
      setWriteError('미리보기 프레임에 접근할 수 없습니다');
      return;
    }

    /*
     * 초마다 다시 쓰던 문서를, 시각 따라 바뀌는 부분(시계·카운트다운·구역의 지금 장)만 달라졌으면 그 자리에서 고친다
     * (2026-11-04) — 다시 쓰면 떠 있는 동안의 움직임이 초마다 처음부터 다시 돌아 빛줄기가 끝까지 가지 못한다.
     */
    const key = templateLiveKey(document_);
    if (written.current?.doc === doc && written.current.key === key && doc.body) {
      patchLiveParts(doc, document_);
      return;
    }
    doc.open();
    doc.write(document_);
    doc.close();
    written.current = { doc, key };
    setWriteError(null);
    // HTML 칸 — 다시 그릴 때마다(초마다) 재지만, 자리가 같으면 겹친 미리보기를 건드리지 않는다(다시 불러오지 않게).
    const slots = measureHtmlSlots(doc);
    setHtmlSlots((known) => (sameSlots(known, slots) ? known : slots));
  }, [document_]);


  return (
    <div ref={hostRef} className={`tpl-preview ${fill ? 'tpl-preview--fill' : ''}`}>
      {renderError ?? writeError ? (
        <p className="tpl-preview__error">{renderError ?? writeError}</p>
      ) : null}
      <iframe
        ref={frameRef}
        title="템플릿 미리보기"
        /*
         * `allow-same-origin` 만 준다.
         *
         * 스크립트는 여전히 차단된다(`allow-scripts` 가 없으므로) — 이스케이프가
         * 뚫리더라도 주입된 스크립트가 실행되지 않는다. 대신 부모가
         * `contentDocument` 에 직접 쓸 수 있어, 입력할 때마다 iframe 을 다시
         * 로드하지 않고 문서만 갈아끼운다.
         *
         * `sandbox=""` 로 두면 origin 이 불투명해져 부모가 문서에 접근할 수 없고,
         * 미리보기가 **빈 채로 남는다**. `srcdoc` 으로 바꾸면 갱신마다 프레임이
         * 다시 로드돼 타이핑 중에 깜빡인다.
         */
        sandbox="allow-same-origin"
        loading="lazy"
      />
      {htmlSlots.map((slot) => {
        const info = infos[slot.id];
        if (!info?.preview_url) return null;
        const sized = itemHtmlFit(info.html_fit, info.width, info.height) !== undefined;
        return (
          <div
            // 같은 HTML 을 두 구역에 넣을 수 있다 — 자리까지 붙여 가른다.
            key={`${slot.id}@${Math.round(slot.left)},${Math.round(slot.top)}`}
            // 미리보기 문서 밖에 겹친 칸이라 문서의 움직임을 따라가지 않는다 — 같은 때에 함께 나타나게.
            className={`tpl-preview__html${entering ? ' tpl-preview__html--enter' : ''}`}
            style={{ left: slot.left, top: slot.top, width: slot.width, height: slot.height, borderRadius: slot.radius }}
            aria-hidden="true"
          >
            <HtmlFrame
              url={info.preview_url}
              title="HTML 칸"
              className="h-full w-full"
              screen={slot.pane}
              design={sized ? { width: info.width as number, height: info.height as number } : null}
              // 맞춤·보일 영역은 칸에 적힌 대로 — 기준 크기가 있는 HTML 에만 건다(재생 화면과 같게).
              fit={sized ? slot.fit : undefined}
              area={slot.area}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * 시각 따라 바뀌는 부분만 새 문서의 것으로 옮긴다(`templateLiveKey` 가 비우는 곳) — 머리글 날짜·시각, 프로모션 카운트다운,
 * 구역 나누기의 지금 장. 같은 렌더러가 그린 문서라 차례가 같다. 장 표시가 바뀌면 화면처럼 페이드로 넘어간다.
 */
function patchLiveParts(doc: Document, html: string): void {
  const next = new DOMParser().parseFromString(html, 'text/html');
  for (const selector of ['.so-tpl__date', '.so-tpl__time', '.so-promo__countdown-value']) {
    const source = next.querySelectorAll(selector);
    doc.querySelectorAll(selector).forEach((element, index) => {
      const text = source[index]?.textContent ?? '';
      if (element.textContent !== text) element.textContent = text;
    });
  }
  const slides = next.querySelectorAll('.so-zone__slide');
  doc.querySelectorAll('.so-zone__slide').forEach((element, index) => {
    const name = slides[index]?.className;
    if (name !== undefined && element.className !== name) element.className = name;
  });
}

/** 미리보기 비율(16:9). 목록 카드가 이 비율로 자리를 잡는다. */
export const PREVIEW_ASPECT = CANVAS_WIDTH / CANVAS_HEIGHT;
